import React, { useState, useEffect, useCallback } from 'react';
import TabBar from '../components/TabBar';

function getToken() { return window.__cafeApiToken || localStorage.getItem('cafepos_token'); }
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function Payables() {
  const [tab, setTab] = useState('list');
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', item? }
  const [toast, setToast] = useState('');
  const [showPaid, setShowPaid] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/payables', { headers: authH() });
      setPayables(await res.json());
    } catch { showToast('Błąd ładowania danych'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form) {
    const isEdit = !!form.id;
    const url = isEdit ? `/api/payables/${form.id}` : '/api/payables';
    const method = isEdit ? 'PATCH' : 'POST';
    const res = await fetch(url, {
      method, headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    setModal(null);
    showToast(isEdit ? '✓ Zapisano zmiany' : '✓ Dodano fakturę');
    load();
  }

  async function markPaid(item) {
    await fetch(`/api/payables/${item.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify({ status: 'paid' }),
    });
    showToast('✓ Oznaczono jako zapłacone');
    load();
  }

  async function markUnpaid(item) {
    await fetch(`/api/payables/${item.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authH() },
      body: JSON.stringify({ status: 'unpaid' }),
    });
    load();
  }

  async function handleDelete(item) {
    if (!window.confirm(`Usunąć fakturę „${item.invoice_number}" (${item.supplier_name})?`)) return;
    await fetch(`/api/payables/${item.id}`, { method: 'DELETE', headers: authH() });
    showToast('✓ Usunięto fakturę');
    load();
  }

  async function syncKsef() {
    setSyncing(true);
    try {
      const to = todayISO();
      const from = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      const res = await fetch('/api/payables/sync-ksef', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authH() },
        body: JSON.stringify({ from, to }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      showToast(`✓ Pobrano ${data.imported} nowych, pominięto ${data.skipped} (już były)${data.failed ? `, ${data.failed} błędów` : ''}`);
      load();
    } catch (err) { showToast(`✗ Błąd synchronizacji: ${err.message}`); }
    finally { setSyncing(false); }
  }

  if (loading) return <div className="page-loading">Ładowanie...</div>;

  const today = todayISO();
  const unpaid = payables.filter(p => p.status === 'unpaid');
  const overdue = unpaid.filter(p => p.due_date < today);
  const dueSoon = unpaid.filter(p => p.due_date >= today && p.due_date <= addDays(today, 7));

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>🧾 Terminarz płatności</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={syncKsef} disabled={syncing}>
            {syncing ? 'Synchronizuję...' : '🔄 Pobierz z KSeF'}
          </button>
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
            onClick={() => setModal({ mode: 'add' })}>
            + Nowa faktura
          </button>
        </div>
      </div>

      {(overdue.length > 0 || dueSoon.length > 0) && (
        <div style={{
          display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16,
        }}>
          {overdue.length > 0 && (
            <div style={{ flex: 1, minWidth: 200, background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#A32D2D' }}>⚠ Zaległe: {overdue.length}</div>
              <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 2 }}>
                {overdue.reduce((s, p) => s + Number(p.amount), 0).toFixed(2)} zł do zapłaty
              </div>
            </div>
          )}
          {dueSoon.length > 0 && (
            <div style={{ flex: 1, minWidth: 200, background: '#FAEEDA', border: '0.5px solid #F0D8A8', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#854F0B' }}>⏳ Termin w ciągu 7 dni: {dueSoon.length}</div>
              <div style={{ fontSize: 12, color: '#854F0B', marginTop: 2 }}>
                {dueSoon.reduce((s, p) => s + Number(p.amount), 0).toFixed(2)} zł do zapłaty
              </div>
            </div>
          )}
        </div>
      )}

      <TabBar tabs={[{ id: 'list', label: '📋 Lista' }, { id: 'calendar', label: '📅 Kalendarz' }]} active={tab} onChange={setTab} />

      {tab === 'list' && (
        <ListView payables={payables} showPaid={showPaid} setShowPaid={setShowPaid} today={today}
          onEdit={item => setModal({ mode: 'edit', item })} onMarkPaid={markPaid} onMarkUnpaid={markUnpaid} onDelete={handleDelete} />
      )}
      {tab === 'calendar' && (
        <CalendarView payables={payables} today={today}
          onEdit={item => setModal({ mode: 'edit', item })} onMarkPaid={markPaid} />
      )}

      {modal && (
        <PayableModal item={modal.item} onSave={handleSave} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Widok listy - pogrupowane: zaległe, w ciągu 7 dni, później, zapłacone (zwinięte)
// ---------------------------------------------------------------------------
function ListView({ payables, showPaid, setShowPaid, today, onEdit, onMarkPaid, onMarkUnpaid, onDelete }) {
  const unpaid = payables.filter(p => p.status === 'unpaid').sort((a, b) => a.due_date.localeCompare(b.due_date));
  const paid   = payables.filter(p => p.status === 'paid').sort((a, b) => b.due_date.localeCompare(a.due_date));

  if (payables.length === 0) {
    return <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)' }}>Brak faktur kosztowych</div>;
  }

  return (
    <div>
      {unpaid.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Brak niezapłaconych faktur 🎉</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {unpaid.map(p => (
            <PayableRow key={p.id} p={p} today={today} onEdit={() => onEdit(p)} onMarkPaid={() => onMarkPaid(p)} onDelete={() => onDelete(p)} />
          ))}
        </div>
      )}

      <button className="btn-sm" onClick={() => setShowPaid(s => !s)}>
        {showPaid ? '▲ Ukryj zapłacone' : `▼ Pokaż zapłacone (${paid.length})`}
      </button>

      {showPaid && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          {paid.map(p => (
            <PayableRow key={p.id} p={p} today={today} paidView onEdit={() => onEdit(p)} onMarkUnpaid={() => onMarkUnpaid(p)} onDelete={() => onDelete(p)} />
          ))}
        </div>
      )}
    </div>
  );
}

const PAYMENT_METHOD_LABELS = { cash: '💵 Gotówka', card: '💳 Karta', transfer: '🏦 Przelew', blik: '📱 BLIK', other: 'Inna' };

function PayableRow({ p, today, paidView, onEdit, onMarkPaid, onMarkUnpaid, onDelete }) {
  const isOverdue = !paidView && p.due_date < today;
  const dueLabel = new Date(p.due_date + 'T00:00:00').toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div style={{
      background: 'var(--surface)', border: `0.5px solid ${isOverdue ? '#F09595' : 'var(--border)'}`,
      borderRadius: 12, padding: 14, opacity: paidView ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{p.supplier_name}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.invoice_number}</span>
            {isOverdue && <span style={{ fontSize: 11, fontWeight: 600, color: '#A32D2D', background: '#FCEBEB', padding: '2px 8px', borderRadius: 12 }}>ZALEGŁA</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Termin: {dueLabel}{p.payment_method ? ` · ${PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}` : ''}{p.ksef_number ? ` · KSeF: ${p.ksef_number}` : ''}
          </div>
          {p.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>„{p.notes}"</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{Number(p.amount).toFixed(2)} zł</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={onEdit} className="btn-sm">✏️</button>
            <button onClick={onDelete} className="btn-sm danger">🗑</button>
            {paidView
              ? <button onClick={onMarkUnpaid} className="btn-sm">Cofnij</button>
              : <button onClick={onMarkPaid} className="btn-sm success">Zapłacone</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widok kalendarza - siatka miesięczna, dni z terminami płatności podświetlone
// ---------------------------------------------------------------------------
function CalendarView({ payables, today, onEdit, onMarkPaid }) {
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);

  const firstOfMonth = new Date(month.y, month.m, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // poniedziałek = 0
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();

  const byDay = payables.reduce((acc, p) => {
    (acc[p.due_date] = acc[p.due_date] || []).push(p);
    return acc;
  }, {});

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function dayKey(d) { return `${month.y}-${String(month.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }
  function prevMonth() { setMonth(m => m.m === 0 ? { y: m.y - 1, m: 11 } : { y: m.y, m: m.m - 1 }); setSelectedDay(null); }
  function nextMonth() { setMonth(m => m.m === 11 ? { y: m.y + 1, m: 0 } : { y: m.y, m: m.m + 1 }); setSelectedDay(null); }

  const monthLabel = firstOfMonth.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
  const WEEKDAYS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 14 }}>
        <button className="btn-sm" onClick={prevMonth}>◀</button>
        <span style={{ fontSize: 15, fontWeight: 600, textTransform: 'capitalize', minWidth: 160, textAlign: 'center' }}>{monthLabel}</span>
        <button className="btn-sm" onClick={nextMonth}>▶</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {WEEKDAYS.map(w => (
          <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>{w}</div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const key = dayKey(d);
          const items = byDay[key] || [];
          const hasUnpaid = items.some(p => p.status === 'unpaid');
          const isToday = key === today;
          const isPast = key < today;
          return (
            <div key={i} onClick={() => items.length && setSelectedDay(selectedDay === key ? null : key)}
              style={{
                minHeight: 62, borderRadius: 8, padding: '6px 6px', cursor: items.length ? 'pointer' : 'default',
                border: isToday ? '1.5px solid var(--blue)' : '0.5px solid var(--border)',
                background: selectedDay === key ? 'var(--blue-light)' : hasUnpaid ? (isPast ? '#FCEBEB' : '#FAEEDA') : 'var(--surface)',
              }}>
              <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--blue-dark)' : 'var(--text)' }}>{d}</div>
              {items.length > 0 && (
                <div style={{ fontSize: 10, marginTop: 2, color: hasUnpaid ? (isPast ? '#A32D2D' : '#854F0B') : 'var(--text-muted)' }}>
                  {items.length} · {items.reduce((s, p) => s + Number(p.amount), 0).toFixed(0)} zł
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedDay && byDay[selectedDay] && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
            {new Date(selectedDay + 'T00:00:00').toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: 'long' })}
          </div>
          {byDay[selectedDay].map(p => (
            <div key={p.id} onClick={() => onEdit(p)} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.supplier_name} · {p.invoice_number}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.status === 'paid' ? 'Zapłacone' : 'Do zapłaty'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 600 }}>{Number(p.amount).toFixed(2)} zł</span>
                {p.status === 'unpaid' && (
                  <button onClick={e => { e.stopPropagation(); onMarkPaid(p); }} className="btn-sm success">Zapłacone</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal dodawania/edycji faktury kosztowej
// ---------------------------------------------------------------------------
function PayableModal({ item, onSave, onClose }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    supplier_name:   item?.supplier_name   || '',
    supplier_nip:    item?.supplier_nip    || '',
    invoice_number:  item?.invoice_number  || '',
    ksef_number:     item?.ksef_number     || '',
    amount:          item?.amount          || '',
    issue_date:      item?.issue_date ? String(item.issue_date).split('T')[0] : '',
    due_date:        item?.due_date ? String(item.due_date).split('T')[0] : todayISO(),
    payment_method:  item?.payment_method || '',
    notes:           item?.notes           || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.supplier_name.trim() && form.invoice_number.trim() && Number(form.amount) > 0 && form.due_date;

  async function handleSubmit() {
    setSaving(true);
    await onSave({ id: item?.id, ...form, amount: Number(form.amount) });
    setSaving(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 420, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{isEdit ? 'Edytuj fakturę' : 'Nowa faktura kosztowa'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div className="field">
          <label>Dostawca *</label>
          <input value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)} placeholder="np. Hurtownia XYZ Sp. z o.o." />
        </div>
        <div className="field">
          <label>NIP dostawcy</label>
          <input value={form.supplier_nip} onChange={e => set('supplier_nip', e.target.value)} placeholder="0000000000" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field">
            <label>Nr faktury *</label>
            <input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="FV/123/2026" />
          </div>
          <div className="field">
            <label>Kwota (zł) *</label>
            <input type="number" step="0.01" min="0" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
          <div className="field">
            <label>Data wystawienia</label>
            <input type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} />
          </div>
          <div className="field">
            <label>Termin płatności *</label>
            <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label>Forma płatności</label>
          <select value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
            <option value="">— nie wybrano —</option>
            <option value="cash">💵 Gotówka</option>
            <option value="card">💳 Karta</option>
            <option value="transfer">🏦 Przelew</option>
            <option value="blik">📱 BLIK</option>
            <option value="other">Inna</option>
          </select>
        </div>
        <div className="field">
          <label>Numer KSeF <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(opcjonalnie)</span></label>
          <input value={form.ksef_number} onChange={e => set('ksef_number', e.target.value)} placeholder="np. 5252445767-20260201-XYZ789ABC123" />
        </div>
        <div className="field">
          <label>Uwagi</label>
          <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="np. czynsz za lokal, sierpień" />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '11px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
            Anuluj
          </button>
          <button onClick={handleSubmit} disabled={!valid || saving}
            style={{ flex: 2, padding: '11px', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
              background: valid ? 'var(--blue)' : 'var(--border)',
              color: valid ? '#fff' : 'var(--text-muted)',
              cursor: valid ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Zapisuję...' : (isEdit ? 'Zapisz zmiany' : 'Dodaj fakturę')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a1a', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, zIndex: 999 }}>
      {msg}
    </div>
  );
}
